import {Trend} from 'platform/components';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {SalesLeagueBranchBoardResponse} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps} from 'shared/web';

import {SalesLeagueKpiItem, SalesLeagueKpiPanel} from '../../../components/SalesLeagueKpiPanel';
import {
  SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
  SALES_LEAGUE_KPI_BEST_MARGIN_COLOR,
  SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
  SALES_LEAGUE_KPI_VEHICLES_COLOR,
} from '../../../constants/SALES_LEAGUE_KPI_ACCENT_COLORS';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {parseMarginAmount} from '../../../utils/parseMarginAmount';
import {parseRatio} from '../../../utils/parseRatio';
import {getPeriodChangePercentage} from '../utils/getPeriodChangePercentage';

const PERCENTAGE_DECIMALS = 0;
const VEHICLES_PER_BRANCH_DECIMALS = 1;
const NO_CHANGE = 0;
const POSITIVE_SIGN = '+';
const PERCENTAGE_SUFFIX = '%';

interface SalesLeagueBranchBoardKpisProps extends RequiredTestIdProps {
  branchBoard: SalesLeagueBranchBoardResponse;
  periodLabel: string;
}

export function SalesLeagueBranchBoardKpis(props: SalesLeagueBranchBoardKpisProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  const formatMargin = (amount: string | Nullish) =>
    formatMarginAmount(amount, props.branchBoard.currency, formatCurrency);

  const totals = props.branchBoard.totals;

  // The branch holding the highest per-vehicle margin. The backend resolves the maximum and
  // its tie-break, so this is only a name lookup — never re-scan the rows for it.
  const bestMarginBranch = props.branchBoard.rows.find(
    (row) => row.branchId === totals.bestMarginBranchId
  );

  const vehiclesPerBranch = parseRatio(totals.vehiclesPerBranch);

  const renderChangeChip = (change: number | null) => {
    if (isNil(change)) {
      return undefined;
    }

    const formattedChange = formatNumber(change, PERCENTAGE_DECIMALS) ?? EMPTY_PLACEHOLDER;
    const signedChange =
      change > NO_CHANGE ? `${POSITIVE_SIGN}${formattedChange}` : formattedChange;

    return (
      <Trend
        label={`${signedChange} ${PERCENTAGE_SUFFIX}`}
        isPositive={change > NO_CHANGE}
        isNegative={change < NO_CHANGE}
        isSubtle
      />
    );
  };

  const totalVehiclesSoldCard: SalesLeagueKpiItem = {
    id: 'totalVehiclesSold',
    accentColor: SALES_LEAGUE_KPI_VEHICLES_COLOR,
    label: i18n.t('page.salesLeague.kpi.totalVehiclesSold'),
    value: formatNumber(totals.vehiclesSold),
    unit: i18n.t('page.salesLeague.kpi.totalVehiclesSoldUnit'),
    footer: i18n.t('page.salesLeague.kpi.vsPreviousPeriod'),
    changeChip: renderChangeChip(
      getPeriodChangePercentage(totals.vehiclesSold, totals.previousVehiclesSold)
    ),
  };

  // The chip belongs to the figure it describes: the total against the previous total,
  // not the per-vehicle average, which now has a tile of its own.
  const totalMarginCard: SalesLeagueKpiItem = {
    id: 'totalMargin',
    accentColor: SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
    label: i18n.t('entity.order.labels.totalMargin'),
    value: formatMargin(totals.totalMargin),
    footer: i18n.t('page.salesLeague.kpi.vsPreviousPeriod'),
    changeChip: renderChangeChip(
      getPeriodChangePercentage(
        parseMarginAmount(totals.totalMargin),
        parseMarginAmount(totals.previousTotalMargin)
      )
    ),
  };

  const avgMarginCard: SalesLeagueKpiItem = {
    id: 'avgMargin',
    accentColor: SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.avgMarginPerVehicle'),
    value: formatMargin(totals.avgMarginPerVehicle),
    footer: i18n.t('page.salesLeague.branchBoard.kpi.avgMarginFooter'),
  };

  const bestMarginCard: SalesLeagueKpiItem = {
    id: 'bestMargin',
    accentColor: SALES_LEAGUE_KPI_BEST_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.kpi.bestMargin'),
    value: formatMargin(totals.bestMarginPerVehicle),
    footer: bestMarginBranch?.branchName ?? EMPTY_PLACEHOLDER,
  };

  const branchesCard: SalesLeagueKpiItem = {
    id: 'branches',
    accentColor: SALES_LEAGUE_KPI_TOTAL_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.branchBoard.kpi.branches'),
    value: formatNumber(totals.branches),
    footer: props.periodLabel,
  };

  const vehiclesPerBranchCard: SalesLeagueKpiItem = {
    id: 'vehiclesPerBranch',
    accentColor: SALES_LEAGUE_KPI_AVG_MARGIN_COLOR,
    label: i18n.t('page.salesLeague.branchBoard.kpi.vehiclesPerBranch'),
    value: isNil(vehiclesPerBranch)
      ? undefined
      : formatNumber(vehiclesPerBranch, VEHICLES_PER_BRANCH_DECIMALS),
    footer: props.periodLabel,
  };

  /*
    Every tile here follows BOARD-LEVEL visibility: the backend populates all three totals
    margin fields — the best-margin pair included — only when margin is visible for every
    branch, because a visible total minus the visible rows would reveal a hidden branch's
    margin. Where they drop out, two figures that need no margin take their place so the
    row keeps its shape. The table and podium still follow the ROWS; that split is the
    point. See docs/adr/0001-margin-visibility-is-gated-at-two-levels.md.
  */
  const totalsCards: SalesLeagueKpiItem[] = props.branchBoard.marginVisible
    ? [avgMarginCard, totalMarginCard]
    : [branchesCard, vehiclesPerBranchCard];

  const cards: SalesLeagueKpiItem[] = [
    totalVehiclesSoldCard,
    ...totalsCards,
    ...(isNotNil(totals.bestMarginPerVehicle) ? [bestMarginCard] : []),
  ];

  return <SalesLeagueKpiPanel items={cards} data-testid={props['data-testid']} />;
}
